import * as Schema from "effect/Schema";

import {
  CommandId,
  IsoDateTime,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";
import { ProviderDriverKind, ProviderInstanceId } from "./providerInstance.ts";

export const ScheduledJobId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._-]*$/),
);
export type ScheduledJobId = typeof ScheduledJobId.Type;

export const ScheduledJobWeekday = Schema.Literals([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
export type ScheduledJobWeekday = typeof ScheduledJobWeekday.Type;

export const ScheduledJobRecurrence = Schema.Union([
  Schema.Struct({ times: Schema.optionalKey(Schema.Undefined), kind: Schema.Literal("daily") }),
  Schema.Struct({
    times: Schema.optionalKey(Schema.Undefined),
    kind: Schema.Literal("weekly"),
    weekday: ScheduledJobWeekday,
  }),
  Schema.Struct({
    times: Schema.optionalKey(Schema.Undefined),
    kind: Schema.Literal("interval"),
    intervalDays: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 365 })),
  }),
]);
export type ScheduledJobRecurrence = typeof ScheduledJobRecurrence.Type;

export const ScheduledJobTarget = Schema.TaggedStruct("ProviderPrompt", {
  taskPrompt: TrimmedNonEmptyString.check(Schema.isMaxLength(20_000)),
  provider: Schema.Literals(["codex", "claudeAgent"]),
});
export type ScheduledJobTarget = typeof ScheduledJobTarget.Type;

export const ScheduledJobDefinition = Schema.Struct({
  // Portable content cannot supply Environment-owned execution or authorization state.
  activation: Schema.optionalKey(Schema.Undefined),
  wakePlan: Schema.optionalKey(Schema.Undefined),
  runHistory: Schema.optionalKey(Schema.Undefined),
  logs: Schema.optionalKey(Schema.Undefined),
  recovery: Schema.optionalKey(Schema.Undefined),
  approval: Schema.optionalKey(Schema.Undefined),
  hostId: Schema.optionalKey(Schema.Undefined),

  jobId: ScheduledJobId,
  displayName: TrimmedNonEmptyString.check(Schema.isMaxLength(120)),
  target: ScheduledJobTarget,
  publication: Schema.optional(Schema.Literal("git")),
  recurrence: ScheduledJobRecurrence,
  localTime: Schema.String.check(Schema.isPattern(/^(?:[01]\d|2[0-3]):[0-5]\d$/)),
  timeZone: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  missedPolicy: Schema.Literals(["skip", "run-once"]),
});
export type ScheduledJobDefinition = typeof ScheduledJobDefinition.Type;

export const ScheduledJobRevision = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));
export type ScheduledJobRevision = typeof ScheduledJobRevision.Type;

export const ScheduledJobDefinitionReadModel = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  definition: ScheduledJobDefinition,
  revision: ScheduledJobRevision,
});
export type ScheduledJobDefinitionReadModel = typeof ScheduledJobDefinitionReadModel.Type;

export const ScheduleListDefinitionsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ScheduleListDefinitionsInput = typeof ScheduleListDefinitionsInput.Type;

export const ScheduleInvalidDefinition = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  detail: TrimmedNonEmptyString,
});
export type ScheduleInvalidDefinition = typeof ScheduleInvalidDefinition.Type;

export const ScheduleListDefinitionsResult = Schema.Struct({
  definitions: Schema.Array(ScheduledJobDefinitionReadModel),
  invalidDefinitions: Schema.Array(ScheduleInvalidDefinition),
});
export type ScheduleListDefinitionsResult = typeof ScheduleListDefinitionsResult.Type;

export const ScheduleSaveDefinitionInput = Schema.Struct({
  // Retries of one client mutation reuse this durable command identity.
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  definition: ScheduledJobDefinition,
  existingRelativePath: Schema.NullOr(TrimmedNonEmptyString),
  // The client's last observed definition revision; null asserts absence on Create.
  expectedRevision: Schema.NullOr(ScheduledJobRevision),
});
export type ScheduleSaveDefinitionInput = typeof ScheduleSaveDefinitionInput.Type;

export const ScheduleSaveDefinitionResult = Schema.Struct({
  saved: ScheduledJobDefinitionReadModel,
  verified: Schema.Literal(true),
  staged: Schema.Literal(false),
  activationCreated: Schema.Literal(false),
  agentInterviewStarted: Schema.Literal(false),
  workspaceInstructionProfileCreated: Schema.Literal(false),
});
export type ScheduleSaveDefinitionResult = typeof ScheduleSaveDefinitionResult.Type;

export const ScheduleDeleteDefinitionInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
  relativePath: TrimmedNonEmptyString,
  expectedRevision: Schema.NullOr(ScheduledJobRevision),
});
export type ScheduleDeleteDefinitionInput = typeof ScheduleDeleteDefinitionInput.Type;

export const ScheduleDeleteDefinitionResult = Schema.Struct({
  jobId: ScheduledJobId,
  relativePath: TrimmedNonEmptyString,
  revision: ScheduledJobRevision,
  verified: Schema.Literal(true),
  staged: Schema.Literal(false),
});
export type ScheduleDeleteDefinitionResult = typeof ScheduleDeleteDefinitionResult.Type;

export const UnattendedExecutionAcceptance = Schema.Struct({
  providerApprovalBypassAccepted: Schema.Literal(true),
  sandboxBypassAccepted: Schema.Literal(true),
});
export type UnattendedExecutionAcceptance = typeof UnattendedExecutionAcceptance.Type;

export const HostScheduleActivationDesiredState = Schema.Literals(["enabled", "paused", "retired"]);
export type HostScheduleActivationDesiredState = typeof HostScheduleActivationDesiredState.Type;

export const HostWakeBlockReason = Schema.Literals([
  "wake-platform-unsupported",
  "wake-scheduler-unavailable",
  "wake-permission-denied",
  "wake-installation-failed",
  "wake-verification-failed",
]);
export type HostWakeBlockReason = typeof HostWakeBlockReason.Type;

export const HostWakePlanReadModel = Schema.Union([
  Schema.TaggedStruct("Verified", {
    planId: TrimmedNonEmptyString,
    mechanism: Schema.Literal("macos-pmset"),
    scheduledAt: IsoDateTime,
    installedAt: IsoDateTime,
    verifiedAt: IsoDateTime,
  }),
  Schema.TaggedStruct("Unavailable", {
    planId: TrimmedNonEmptyString,
    reason: HostWakeBlockReason,
    detail: TrimmedNonEmptyString,
    checkedAt: IsoDateTime,
  }),
]);
export type HostWakePlanReadModel = typeof HostWakePlanReadModel.Type;

export const HostScheduleActivationObservedCondition = Schema.Union([
  Schema.TaggedStruct("Inactive", {}),
  Schema.TaggedStruct("Ready", {}),
  Schema.TaggedStruct("Blocked", {
    reason: HostWakeBlockReason,
    detail: TrimmedNonEmptyString,
  }),
  Schema.TaggedStruct("StaleProject", {
    detail: TrimmedNonEmptyString,
  }),
  Schema.TaggedStruct("MissingDefinition", {
    detail: TrimmedNonEmptyString,
  }),
]);
export type HostScheduleActivationObservedCondition =
  typeof HostScheduleActivationObservedCondition.Type;

export const HostScheduleActivationReadModel = Schema.Struct({
  projectId: ProjectId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
  definitionRevision: ScheduledJobRevision,
  desiredState: HostScheduleActivationDesiredState,
  providerInstanceId: Schema.optional(ProviderInstanceId),
  providerDriver: Schema.optional(ProviderDriverKind),
  unattendedExecutionAcceptedAt: IsoDateTime,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  nextScheduledAt: Schema.NullOr(IsoDateTime),
  observedCondition: HostScheduleActivationObservedCondition,
});
export type HostScheduleActivationReadModel = typeof HostScheduleActivationReadModel.Type;

export const ScheduleSaveAndEnableInput = Schema.Struct({
  ...ScheduleSaveDefinitionInput.fields,
  projectId: ProjectId,
  providerInstanceId: Schema.optional(ProviderInstanceId),
  unattendedExecutionAcceptance: UnattendedExecutionAcceptance,
});
export type ScheduleSaveAndEnableInput = typeof ScheduleSaveAndEnableInput.Type;

export const ScheduleSaveAndEnableResult = Schema.Struct({
  saved: ScheduledJobDefinitionReadModel,
  verified: Schema.Literal(true),
  staged: Schema.Literal(false),
  activation: HostScheduleActivationReadModel,
  wakePlan: Schema.NullOr(HostWakePlanReadModel),
  agentInterviewStarted: Schema.Literal(false),
  workspaceInstructionProfileCreated: Schema.Literal(false),
});
export type ScheduleSaveAndEnableResult = typeof ScheduleSaveAndEnableResult.Type;

export const ScheduleListActivationsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ScheduleListActivationsInput = typeof ScheduleListActivationsInput.Type;

export const ScheduleListActivationsResult = Schema.Struct({
  activations: Schema.Array(HostScheduleActivationReadModel),
  wakePlan: Schema.NullOr(HostWakePlanReadModel),
});
export type ScheduleListActivationsResult = typeof ScheduleListActivationsResult.Type;

export const ScheduleHostSummaryEntry = Schema.Struct({
  projectId: ProjectId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
  displayName: TrimmedNonEmptyString.check(Schema.isMaxLength(120)),
  nextScheduledAt: Schema.NullOr(IsoDateTime),
});
export type ScheduleHostSummaryEntry = typeof ScheduleHostSummaryEntry.Type;

export const ScheduleHostSummaryResult = Schema.Struct({
  activeCount: NonNegativeInt,
  nextScheduledAt: Schema.NullOr(IsoDateTime),
  schedules: Schema.Array(ScheduleHostSummaryEntry),
});
export type ScheduleHostSummaryResult = typeof ScheduleHostSummaryResult.Type;

export const ScheduleSetActivationDesiredStateInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
  desiredState: HostScheduleActivationDesiredState,
});
export type ScheduleSetActivationDesiredStateInput =
  typeof ScheduleSetActivationDesiredStateInput.Type;

export const ScheduleSetActivationDesiredStateResult = Schema.Struct({
  activation: HostScheduleActivationReadModel,
  wakePlan: Schema.NullOr(HostWakePlanReadModel),
});
export type ScheduleSetActivationDesiredStateResult =
  typeof ScheduleSetActivationDesiredStateResult.Type;

export const ScheduleDeleteActivationInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
});
export type ScheduleDeleteActivationInput = typeof ScheduleDeleteActivationInput.Type;

export const ScheduleDeleteActivationResult = Schema.Struct({
  jobId: ScheduledJobId,
  deleted: Schema.Literal(true),
  wakePlan: Schema.NullOr(HostWakePlanReadModel),
});
export type ScheduleDeleteActivationResult = typeof ScheduleDeleteActivationResult.Type;

export const ScheduledOccurrenceId = Schema.String.check(Schema.isPattern(/^occ_[0-9a-f]{64}$/));
export type ScheduledOccurrenceId = typeof ScheduledOccurrenceId.Type;

export const ScheduledOccurrenceProjectRevision = Schema.String.check(
  Schema.isPattern(/^[0-9a-f]{40,64}$/),
);
export type ScheduledOccurrenceProjectRevision = typeof ScheduledOccurrenceProjectRevision.Type;

export const ScheduledOccurrenceStatus = Schema.Union([
  Schema.TaggedStruct("Runnable", {}),
  Schema.TaggedStruct("Running", {}),
  Schema.TaggedStruct("Completed", {}),
  Schema.TaggedStruct("Failed", {}),
  Schema.TaggedStruct("Cancelled", {}),
  Schema.TaggedStruct("Unresolved", {}),
  Schema.TaggedStruct("SkippedNonexistentLocalTime", {}),
  Schema.TaggedStruct("SkippedMissed", {}),
  Schema.TaggedStruct("SkippedOverlap", {
    overlappingOccurrenceId: ScheduledOccurrenceId,
  }),
]);
export type ScheduledOccurrenceStatus = typeof ScheduledOccurrenceStatus.Type;

export const ScheduledOccurrenceMissedWindow = Schema.Struct({
  earliestMissedAt: IsoDateTime,
  latestMissedAt: IsoDateTime,
  skippedOccurrenceCount: NonNegativeInt,
});
export type ScheduledOccurrenceMissedWindow = typeof ScheduledOccurrenceMissedWindow.Type;

export const ScheduledOccurrenceWakeVerification = Schema.Struct({
  planId: TrimmedNonEmptyString,
  mechanism: Schema.Literal("macos-pmset"),
  scheduledAt: IsoDateTime,
  installedAt: IsoDateTime,
  verifiedAt: IsoDateTime,
});
export type ScheduledOccurrenceWakeVerification = typeof ScheduledOccurrenceWakeVerification.Type;

export const ScheduledOccurrenceReadModel = Schema.Struct({
  occurrenceId: ScheduledOccurrenceId,
  projectId: ProjectId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
  definitionRevision: ScheduledJobRevision,
  definition: ScheduledJobDefinition,
  projectRevision: ScheduledOccurrenceProjectRevision,
  providerInstanceId: Schema.optional(ProviderInstanceId),
  nominalLocalAt: TrimmedNonEmptyString,
  scheduledAt: Schema.NullOr(IsoDateTime),
  materializedAt: IsoDateTime,
  // Optional only for decoding pre-evidence ledgers; new runnable occurrences
  // must carry an exact verified wake before unattended execution.
  wakeVerification: Schema.optional(ScheduledOccurrenceWakeVerification),
  status: ScheduledOccurrenceStatus,
  failure: Schema.optional(TrimmedNonEmptyString),
  missedWindow: Schema.NullOr(ScheduledOccurrenceMissedWindow),
});
export type ScheduledOccurrenceReadModel = typeof ScheduledOccurrenceReadModel.Type;

export const ScheduleListOccurrencesInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  limit: Schema.optional(NonNegativeInt),
});
export type ScheduleListOccurrencesInput = typeof ScheduleListOccurrencesInput.Type;

export const ScheduleListOccurrencesResult = Schema.Struct({
  occurrences: Schema.Array(ScheduledOccurrenceReadModel),
});
export type ScheduleListOccurrencesResult = typeof ScheduleListOccurrencesResult.Type;

export const ScheduleDeleteOccurrenceInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  occurrenceId: ScheduledOccurrenceId,
});
export type ScheduleDeleteOccurrenceInput = typeof ScheduleDeleteOccurrenceInput.Type;

export const ScheduleDeleteOccurrenceResult = Schema.Struct({
  occurrenceId: ScheduledOccurrenceId,
  deleted: Schema.Literal(true),
});
export type ScheduleDeleteOccurrenceResult = typeof ScheduleDeleteOccurrenceResult.Type;

export const ScheduleOccurrenceFailure = Schema.Literals([
  "project-not-admitted",
  "occurrence-not-found",
  "occurrence-active",
  "invalid-transition",
  "operation-failed",
]);
export type ScheduleOccurrenceFailure = typeof ScheduleOccurrenceFailure.Type;

export class ScheduleOccurrenceError extends Schema.TaggedErrorClass<ScheduleOccurrenceError>()(
  "ScheduleOccurrenceError",
  {
    cwd: TrimmedNonEmptyString,
    reason: ScheduleOccurrenceFailure,
    message: TrimmedNonEmptyString,
    occurrenceId: Schema.optional(ScheduledOccurrenceId),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ScheduleActivationFailure = Schema.Literals([
  "activation-not-found",
  "definition-not-found",
  "project-not-admitted",
  "idempotency-conflict",
  "operation-failed",
]);
export type ScheduleActivationFailure = typeof ScheduleActivationFailure.Type;

export class ScheduleActivationError extends Schema.TaggedErrorClass<ScheduleActivationError>()(
  "ScheduleActivationError",
  {
    cwd: TrimmedNonEmptyString,
    reason: ScheduleActivationFailure,
    message: TrimmedNonEmptyString,
    jobId: Schema.optional(ScheduledJobId),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ScheduleDefinitionFailure = Schema.Literals([
  "project-not-admitted",
  "schedule-workspace-unavailable",
  "definition-invalid",
  "definition-not-found",
  "duplicate-job-id",
  "idempotency-conflict",
  "identity-conflict",
  "revision-conflict",
  "unsafe-path",
  "operation-failed",
  "verification-failed",
]);
export type ScheduleDefinitionFailure = typeof ScheduleDefinitionFailure.Type;

export class ScheduleDefinitionError extends Schema.TaggedErrorClass<ScheduleDefinitionError>()(
  "ScheduleDefinitionError",
  {
    cwd: TrimmedNonEmptyString,
    reason: ScheduleDefinitionFailure,
    message: TrimmedNonEmptyString,
    relativePath: Schema.optional(TrimmedNonEmptyString),
    expectedRevision: Schema.optional(ScheduledJobRevision),
    observedRevision: Schema.optional(ScheduledJobRevision),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ScheduleRunId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type ScheduleRunId = typeof ScheduleRunId.Type;

export const ScheduleRunStatus = Schema.Literals([
  "starting",
  "running",
  "waiting-for-approval",
  "waiting-for-user",
  "verifying",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
  "recovering",
  "unresolved",
]);
export type ScheduleRunStatus = typeof ScheduleRunStatus.Type;

export const ScheduleRunLogEntry = Schema.Struct({
  sequence: NonNegativeInt,
  at: IsoDateTime,
  level: Schema.Literals(["info", "warning", "error"]),
  message: TrimmedNonEmptyString,
});
export type ScheduleRunLogEntry = typeof ScheduleRunLogEntry.Type;

export const ScheduleRunVerification = Schema.Struct({
  status: Schema.Literals(["pending", "verified", "failed"]),
  intendedArtifacts: Schema.Array(TrimmedNonEmptyString),
  unstaged: Schema.Boolean,
  publicationRequested: Schema.Boolean,
});
export type ScheduleRunVerification = typeof ScheduleRunVerification.Type;

export const ScheduleRunReadModel = Schema.Struct({
  runId: ScheduleRunId,
  jobId: ScheduledJobId,
  projectId: ProjectId,
  threadId: ThreadId,
  definitionRevision: ScheduledJobRevision,
  definition: ScheduledJobDefinition,
  status: ScheduleRunStatus,
  providerAttemptCount: Schema.Literal(1),
  providerConversationId: Schema.NullOr(TrimmedNonEmptyString),
  /** Opaque provider-owned continuation state, persisted separately from AFAR thread identity. */
  providerResumeCursor: Schema.optional(Schema.Unknown),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  logs: Schema.Array(ScheduleRunLogEntry),
  result: Schema.NullOr(TrimmedNonEmptyString),
  failure: Schema.NullOr(TrimmedNonEmptyString),
  verification: ScheduleRunVerification,
  trigger: Schema.optional(
    Schema.Union([
      Schema.TaggedStruct("Manual", {}),
      Schema.TaggedStruct("Scheduled", {
        occurrenceId: ScheduledOccurrenceId,
        scheduledAt: Schema.NullOr(IsoDateTime),
        projectRevision: ScheduledOccurrenceProjectRevision,
      }),
    ]),
  ),
  activationCreated: Schema.Literal(false),
  automaticRecurrenceStarted: Schema.Boolean,
});
export type ScheduleRunReadModel = typeof ScheduleRunReadModel.Type;

export const ScheduleRunNowInput = Schema.Struct({
  enableHere: Schema.optionalKey(Schema.Undefined),
  recurrence: Schema.optionalKey(Schema.Undefined),
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  jobId: ScheduledJobId,
  expectedRevision: Schema.NullOr(ScheduledJobRevision),
});
export type ScheduleRunNowInput = typeof ScheduleRunNowInput.Type;

export const ScheduleRunNowResult = Schema.Struct({
  run: ScheduleRunReadModel,
  exactDefinitionRevision: Schema.Literal(true),
  activationCreated: Schema.Literal(false),
  automaticRecurrenceStarted: Schema.Literal(false),
});
export type ScheduleRunNowResult = typeof ScheduleRunNowResult.Type;

export const ScheduleListRunsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  jobId: Schema.optional(ScheduledJobId),
  limit: Schema.optional(NonNegativeInt),
});
export type ScheduleListRunsInput = typeof ScheduleListRunsInput.Type;

export const ScheduleListRunsResult = Schema.Struct({
  runs: Schema.Array(ScheduleRunReadModel),
});
export type ScheduleListRunsResult = typeof ScheduleListRunsResult.Type;

export const ScheduleCancelRunInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  runId: ScheduleRunId,
});
export type ScheduleCancelRunInput = typeof ScheduleCancelRunInput.Type;

export const ScheduleResolveRunInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  runId: ScheduleRunId,
  resolution: Schema.Literal("accept-current-state"),
});
export type ScheduleResolveRunInput = typeof ScheduleResolveRunInput.Type;

export const ScheduleRunFailure = Schema.Literals([
  "project-not-admitted",
  "definition-not-found",
  "revision-conflict",
  "provider-not-installed",
  "provider-not-authenticated",
  "provider-unavailable",
  "execution-not-authorized",
  "project-revision-conflict",
  "project-busy",
  "run-not-found",
  "run-not-cancellable",
  "run-not-unresolved",
  "run-still-active",
  "idempotency-conflict",
  "operation-failed",
]);
export type ScheduleRunFailure = typeof ScheduleRunFailure.Type;

export class ScheduleRunError extends Schema.TaggedErrorClass<ScheduleRunError>()(
  "ScheduleRunError",
  {
    cwd: TrimmedNonEmptyString,
    reason: ScheduleRunFailure,
    message: TrimmedNonEmptyString,
    runId: Schema.optional(ScheduleRunId),
    jobId: Schema.optional(ScheduledJobId),
    expectedRevision: Schema.optional(ScheduledJobRevision),
    observedRevision: Schema.optional(ScheduledJobRevision),
    cause: Schema.optional(Schema.Defect()),
  },
) {}
